import {
  DestroyRef,
  Directive,
  inject,
  Input,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { UserService } from '../../dashboard/services/user/user.service';
import { UserModel } from '../models/user/user-model';
import { CompanyService } from '../../dashboard/services/company/company.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[hasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnInit {
  private currentUser!: UserModel;
  private permissions: string[] = [];
  private userRightsAsStrings: string[] = [];
  private activeCompanyId!: number;
  private logicalOp = 'AND';
  private shouldBeDisabled = false;
  private isHidden = true;
  private readonly destroyRef = inject(DestroyRef);
  constructor(
    private readonly templateRef: TemplateRef<unknown>,
    private readonly viewContainer: ViewContainerRef,
    private readonly userService: UserService,
    private readonly companyService: CompanyService,
  ) {}

  ngOnInit() {
    this.companyService
      .getActiveCompany()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((company) => {
        if (company) {
          this.activeCompanyId = company.CompanyId;

          this.userService
            .getUser()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((user) => {
              if (user) {
                this.currentUser = user;
                const userRightsForCompany =
                  this.currentUser.AllUserRights.find(
                    (x) => x.CompanyId === this.activeCompanyId,
                  );

                if (userRightsForCompany) {
                  this.userRightsAsStrings = userRightsForCompany.UserRights;
                }
                this.updateView();
              }
            });
        }
      });
  }

  @Input()
  set hasPermission(val: string[]) {
    this.permissions = val;
    this.updateView();
  }

  @Input()
  set hasPermissionOp(permop: any) {
    this.logicalOp = permop;
    this.updateView();
  }

  @Input()
  set hasPermissionDisabled(shouldBeDisabled: boolean) {
    this.shouldBeDisabled = shouldBeDisabled;
    this.updateView();
  }

  private updateView() {
    if (this.shouldBeDisabled) {
      if (this.checkPermission()) {
        if (this.isHidden) {
          this.enableComponent();
        }
      } else {
        this.disableComponent();
      }
    } else {
      if (this.checkPermission()) {
        if (this.isHidden) {
          this.showComponent();
        }
      } else {
        this.removeComponent();
      }
    }
  }

  showComponent(): void {
    this.viewContainer.createEmbeddedView(this.templateRef);
    this.isHidden = false;
  }
  removeComponent(): void {
    this.isHidden = true;
    this.viewContainer.clear();
  }

  enableComponent(): void {
    this.viewContainer.clear();
    const viewRootElement: HTMLElement = this.viewContainer.createEmbeddedView(
      this.templateRef,
    ).rootNodes[0] as HTMLElement;
    viewRootElement.setAttribute('disabled', 'false');
    this.isHidden = false;
  }

  disableComponent(): void {
    this.isHidden = true;
    this.viewContainer.clear();
    const viewRootElement: HTMLElement = this.viewContainer.createEmbeddedView(
      this.templateRef,
    ).rootNodes[0] as HTMLElement;
    const button = viewRootElement.getElementsByTagName('button').item(0);

    if (button) {
      const icon = viewRootElement
        .getElementsByClassName('k-button-icon')
        .item(0);
      const text = viewRootElement
        .getElementsByClassName('k-button-text')
        .item(0);

      button.setAttribute('disabled', 'true');
      // Kendo 25 / Default v14 class names: no fill modifier, no 'light' color.
      button.classList.remove(
        ...[...button.classList].filter((c) =>
          /^k-button-(primary|secondary|tertiary|info|success|warning|error|inverse)$/.test(
            c,
          ),
        ),
      );
      button.classList.add('k-button-base');
      button.classList.add('w-full');

      if (icon) {
        icon.innerHTML = ``;
      }
      if (text) {
        text.innerHTML = `<span><i class="fa-duotone fa-lock"></i> You are not authorized to perform this action.</span>`;
        // viewRootElement.setAttribute('disabled', 'true');
      }
    } else {
      console.error(
        'No button found in the template. Please make sure you surround the button if you want to disable it with a <div> or <span> tag.',
      );
    }
  }

  private checkPermission() {
    let hasPermission = false;

    if (this.currentUser && this.currentUser.IsSuperUser) {
      return true;
    }
    if (this.currentUser && this.userRightsAsStrings) {
      for (const checkPermission of this.permissions) {
        const permissionFound = this.userRightsAsStrings.find(
          (x) => x.toUpperCase() === checkPermission.toUpperCase(),
        );

        if (permissionFound) {
          hasPermission = true;

          if (this.logicalOp === 'OR') {
            break;
          }
        } else {
          hasPermission = false;
          if (this.logicalOp === 'AND') {
            break;
          }
        }
      }
    }

    return hasPermission;
  }
}
